<template>
  <b-modal
    :id="`${customBubblesGroupId}-modal-id`"
    :title="customBubblesGroupName"
    size="xl"
    @show="onShow"
    @hide="onHide"
  >
    <b-list-group flush>
      <b-list-group-item>
        <b-row class="justify-content-between align-items-center">
          <b-col>
            <b-input v-model="customBubblesGroupName" />
          </b-col>
          <b-col cols="2">
            <b-button
              variant="outline-primary"
              block
              @click="addCustomBubblesGroupFilter"
            >
              <font-awesome-icon icon="plus" />
            </b-button>
          </b-col>
        </b-row>
      </b-list-group-item>
      <b-list-group-item
        v-for="({category, compare}, index) in customBubblesGroupFilters"
        :key="`${customBubblesGroupId}-filter-${index}`"
      >
        <LabelledInput :label="getCustomBubblesGroupFilterLabel(customBubblesGroupFilters[index])">
          <b-checkbox
            v-model="customBubblesGroupFilters[index].compare"
            :value="customBubblesCompareCategory"
            :unchecked-value="null"
            switch
            class="pb-2"
          >
            Compare to another category
          </b-checkbox>
          <b-row class="justify-content-between align-items-center">
            <b-col>
              <b-input-group>
                <b-select
                  v-model="customBubblesGroupFilters[index].category"
                  :options="fieldMapOptions"
                />
                <b-select
                  v-model="customBubblesGroupFilters[index].operator"
                  :options="operatorOptions(category)"
                />
                <b-select
                  v-if="compare === customBubblesCompareCategory"
                  v-model="customBubblesGroupFilters[index].value"
                  :options="isStringField(category) ? stringCategoryOptions : numericalCategoryOptions"
                />
                <b-select
                  v-else-if="isStringField(category)"
                  v-model="customBubblesGroupFilters[index].value"
                  :options="categoryOptions(category)"
                />
                <b-input
                  v-else
                  v-model="customBubblesGroupFilters[index].value"
                />
              </b-input-group>
            </b-col>
            <b-col cols="2">
              <b-button
                variant="outline-danger"
                block
                @click="customBubblesGroupFilters.splice(index, 1)"
              >
                <font-awesome-icon icon="minus" />
              </b-button>
            </b-col>
          </b-row>
        </LabelledInput>
      </b-list-group-item>
    </b-list-group>
  </b-modal>
</template>

<script lang="ts">

import Vue, { PropType } from 'vue'
import Component from 'vue-class-component'

import LabelledInput from '@/components/builder/UI/LabelledInput.vue'
import { BvModalEvent } from 'bootstrap-vue'

import {
  CustomBubblesCompareCategory,
  CustomBubblesFilters,
  CustomBubblesFilter,
  ComparisonEqualityOperators,
  ComparisonEqualityOperator,
  Path,
} from '@/types'

const cloneDeep = require('lodash.clonedeep')

const components = {
  LabelledInput,
}

const props = {
  customBubblesGroupId: {
    type: String,
    required: true,
    default: '',
  },
  customBubblesGroup: {
    type: Object as PropType<CustomBubblesFilters>,
    required: true,
  },
}

@Component({ components })
export default class CustomBubblesGroupFilters extends Vue.extend({ props }) {
  customBubblesCompareCategory = CustomBubblesCompareCategory
  customBubblesGroupName: string = ''
  customBubblesGroupFilters: CustomBubblesFilter[] = []

  get fieldMapOptions () {
    // Without artificial category/column like the calculated fields
    return this.$typedStore.primary.app.config.fieldMap.map(({ name, column }) => ({ text: name, value: column }))
  }

  get stringCategoryOptions () {
    return this.stringFields.map(({ name, column }) => ({ text: name, value: column }))
  }

  get numericalCategoryOptions () {
    return this.numericalFields.map(({ name, column }) => ({ text: name, value: column }))
  }

  get stringFields () {
    return this.$typedStore.visualisationApp.stringFields
  }

  get monetaryFields () {
    return this.$typedStore.visualisationApp.monetaryFields
  }

  get numericFields () {
    return this.$typedStore.visualisationApp.numericFields
  }

  get numericalFields () {
    return [
      ...this.monetaryFields,
      ...this.numericFields,
    ]
  }

  onShow () {
    const settings = cloneDeep(this.customBubblesGroup)
    this.customBubblesGroupName = settings.name
    this.customBubblesGroupFilters = settings.filters
  }

  onHide (bvModalEvt: BvModalEvent) {
    if (bvModalEvt.trigger === 'ok') {
      const settings: CustomBubblesFilters = {
        name: this.customBubblesGroupName,
        filters: this.customBubblesGroupFilters,
      }
      this.$emit('update:custom-bubbles-group', settings)
    }
  }

  isStringField (category: string) {
    return this.stringFields.map(({ column }) => column).includes(category)
  }

  operatorOptions (category: string) {
    return Object.values(ComparisonEqualityOperators)
      .map((operator) => ({ value: operator, text: this.operatorLabel(operator) }))
      .filter(({ value: operator }) => {
        if (this.isStringField(category)) {
          switch (operator) {
            case ComparisonEqualityOperators.STRICT_EQUAL:
              return true
            case ComparisonEqualityOperators.STRICT_UNEQUAL:
              return true
            default:
              return false
          }
        }
        return true
      })
  }

  categoryOptions (category: string) {
    return this.$typedStore.visualisationData.fieldLeafDataUnique(Path.root, category).sort()
  }

  fieldMapName (category: string) {
    return this.$typedStore.visualisationApp.name(category)
  }

  operatorLabel (operator: ComparisonEqualityOperator) {
    switch (operator) {
      case ComparisonEqualityOperators.LESS_THAN:
        return 'below'
      case ComparisonEqualityOperators.GREATER_THAN:
        return 'above'
      case ComparisonEqualityOperators.LESS_THAN_OR_EQUAL:
        return 'below or equal to'
      case ComparisonEqualityOperators.GREATER_THAN_OR_EQUAL:
        return 'above or equal to'
      case ComparisonEqualityOperators.STRICT_EQUAL:
        return 'equal to'
      case ComparisonEqualityOperators.STRICT_UNEQUAL:
        return 'does not equal to'
    }
  }

  getCustomBubblesGroupFilterLabel (filter: CustomBubblesFilter) {
    return `${this.fieldMapName(filter.category)} ${this.operatorLabel(filter.operator)} ${filter.compare === CustomBubblesCompareCategory ? this.fieldMapName(String(filter.value)) : filter.value}`
  }

  addCustomBubblesGroupFilter () {
    const category = this.fieldMapOptions[0].value
    const name = ''
    const value = this.isStringField(category) ? this.categoryOptions(category)[0] ?? 0 : 0
    this.customBubblesGroupFilters.push({
      name,
      category,
      operator: ComparisonEqualityOperators.STRICT_EQUAL,
      value,
      compare: null,
    })
  }
}

</script>
